"use client";

import { useEffect, useRef } from "react";

/**
 * Closes a floating panel when anything outside it scrolls, so it never drifts away from its
 * trigger. Scrolling inside the panel (a long list) keeps it open.
 * @param open - Whether the panel is open.
 * @param close - Closes it.
 * @returns A ref for the panel element.
 * @throws Never.
 */
export function useCloseOnScroll<T extends HTMLElement>(open: boolean, close: () => void): React.RefObject<T> {
  // Hooks
  const panelRef = useRef<T>(null);
  const closeRef = useRef(close);

  // Effects
  useEffect(() => {
    closeRef.current = close;
  }, [close]);

  useEffect(() => {
    if (!open) return;
    const handleScroll = (event: Event) => {
      if (panelRef.current?.contains(event.target as Node)) return;
      closeRef.current();
    };
    window.addEventListener("scroll", handleScroll, { capture: true, passive: true });
    return () => window.removeEventListener("scroll", handleScroll, { capture: true });
  }, [open]);

  return panelRef;
}
